import { MonitorStatus } from '@fe/core';
import { DataResult, State } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { Grid, GridColumn, GridCustomRowProps, GridHandle, GridNoRecords } from '@progress/kendo-react-grid';
import React from 'react';
import Coordinate from '../../../../../common/coordinate';
import { DateTimeFormatter } from '../../../../../formatters';
import { useGotoTargetMaps } from '../../../../../hooks/use-goto-target-maps';
import { CheckmarkOutline, CloseOutline, TrashOutline } from '../../../../../icons/icons';
import { EventListItem, EventListItemType } from '../../../../../monitoring/alerts/event-list/models';
import { AcknowledgeAlertType, MessageType } from '../../../../../services/actor-messages';
import { EventListBackgroundColorMode } from '../../../../../types/settings';
import FlightStatusBadge from '../../../../components/FlightStatusBadge';
import MonitorStatusStripe from '../../../../components/monitor-status/MonitorStatusStripe';
import { OpenInMultiWindowButton } from '../../../../components/multi-window/OpenMultiWindowButton';
import { WorkspaceWindowPanelType } from '../../../../components/multi-window/types';
import { EventBus, useEvent } from '../../../../contexts/event-bus';
import { useAppSelector } from '../../../../hooks/store-hooks';
import { useFlightWorker } from '../../../../hooks/use-flight-worker';
import useMap from '../../../../hooks/use-map';
import { PanelTitleTrailingContent } from '../HomePanel';
import './EventList.css';

export const EVENT_LIST_EXPANDED_WIDTH = 550;
export const EVENT_LIST_COMPACT_WIDTH = 200;

export function EventList() {
    const [state, setState] = React.useState<State>({ take: 50, skip: 0 });
    const [dataResult, setDataResult] = React.useState<DataResult>({ total: 0, data: [] });
    const colorMode = useAppSelector((x) => x.settings.eventListBackgroundColorMode);
    const displayFlightStatus = useAppSelector((x) => x.settings.eventListDisplayFlightStatus);
    const displayMonitorStatus = useAppSelector((x) => x.settings.eventListDisplayMonitorStatus);
    const displayImportantIndicator = useAppSelector((x) => x.settings.eventListDisplayImportantIndicator);
    const expandedView = useAppSelector((x) => x.settings.eventListExpandedView);
    const grid = React.useRef<GridHandle | null>(null);
    const map = useMap();
    const worker = useFlightWorker();
    const { goToAirport, goToFlight } = useGotoTargetMaps();
    const loadData = React.useCallback(
        async (state: State) => {
            const items = await worker.actor.sendAsync({ type: MessageType.queryEventList, data: state });
            setDataResult(items);
            setState(state);
        },
        [worker]
    );

    const refreshGrid = React.useCallback(() => {
        if (!grid.current) {
            return;
        }
        loadData({
            take: grid.current.props.take,
            skip: grid.current.props.skip
        });
    }, [loadData]);

    useEvent('update-end', refreshGrid);

    const dismissAllEvents = React.useCallback(async () => {
        await worker.actor.sendAsync({
            type: MessageType.acknowledgeAlert,
            data: { type: AcknowledgeAlertType.clearAll }
        });
        refreshGrid();
    }, [worker]);

    const RowComponent = React.useMemo(
        () =>
            (expandedView ? RowExpanded : RowCollapsed)({
                colorMode: colorMode,
                displayMonitorStatus,
                displayImportantIndicator,
                displayFlightStatus,
                onClick: (e, item) => {
                    if (item.type === EventListItemType.aircraft) {
                        if (item.id != null) {
                            goToFlight(item.recordId, { source: 'event-list' });
                        }
                    } else if (item.type === EventListItemType.airport) {
                        worker.actor.sendAsync({ type: MessageType.findAirport, data: item.subject }).then((airport) => {
                            if (airport && airport.lat != null && airport.lng != null) {
                                goToAirport(Coordinate.fromLatLng(airport.lat, airport.lng));
                            }
                        });
                    }
                },
                onContextMenu: (e, item) => {
                    if (item.type === EventListItemType.aircraft) {
                        EventBus.fireContextMenu([{ kind: 'flight', flightIds: [item.recordId] }], e);
                    } else {
                        EventBus.fireContextMenu([{ kind: 'airport', airportId: item.subject }], e);
                    }
                },
                onAcknowledge: async (item) => {
                    await worker.actor.sendAsync({
                        type: MessageType.acknowledgeAlert,
                        data: {
                            id: item.id!,
                            type: AcknowledgeAlertType.ack
                        }
                    });
                    refreshGrid();
                },
                onDismiss: async (item) => {
                    await worker.actor.sendAsync({
                        type: MessageType.acknowledgeAlert,
                        data: {
                            id: item.id!,
                            type: AcknowledgeAlertType.dismiss
                        }
                    });
                    refreshGrid();
                }
            }),
        [worker, map, colorMode, displayMonitorStatus, displayImportantIndicator, displayFlightStatus, expandedView]
    );

    return (
        <div className="d-flex flex-column h-100">
            <PanelTitleTrailingContent>
                <div className="d-flex">
                    <OpenInMultiWindowButton type={WorkspaceWindowPanelType.eventList} />
                    <Button
                        title="Clear all"
                        className="button-small"
                        fillMode={'flat'}
                        svgIcon={TrashOutline}
                        onClick={dismissAllEvents}
                    />
                </div>
            </PanelTitleTrailingContent>

            <Grid
                ref={grid}
                className="k-grid-no-header k-grid-no-borders flex-grow-1 min-h-0"
                onDataStateChange={(e) => loadData(e.dataState)}
                data={dataResult}
                dataItemKey="id"
                rows={{
                    groupHeader: () => <></>,
                    data: RowComponent
                }}
                {...state}>
                <GridColumn id="main" field="main" />
                <GridNoRecords>
                    <div></div>
                </GridNoRecords>
            </Grid>
        </div>
    );
}

function RowCollapsed(extra: {
    colorMode: EventListBackgroundColorMode;
    displayMonitorStatus: boolean;
    displayImportantIndicator: boolean;
    displayFlightStatus: boolean;
    onClick: (e: MouseEvent, item: EventListItem) => void;
    onAcknowledge: (item: EventListItem) => void;
    onDismiss: (item: EventListItem) => void;
    onContextMenu: (e: MouseEvent, item: EventListItem) => void;
}) {
    return (props: GridCustomRowProps) => {
        const item = props.dataItem as EventListItem & { eventName: string };

        return (
            <tr {...props.trProps}>
                <td
                    style={getStyle(item, extra.colorMode)}
                    className="p-0 clickable border-bottom"
                    onClick={(e) => extra.onClick(e.nativeEvent, item)}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        extra.onContextMenu(e.nativeEvent, item);
                    }}>
                    <div className="d-flex event-list-item align-items-center">
                        <div className="d-flex align-items-center align-self-stretch gap-1">
                            {extra.displayMonitorStatus && (
                                <div className="align-self-stretch py-1">
                                    <MonitorStatusStripe className="h-100" monitorStatus={item.monitorStatus} />
                                </div>
                            )}

                            {extra.displayImportantIndicator && item.important && (
                                <div className="event-list-important" style={{ color: item.color }}>
                                    !
                                </div>
                            )}
                        </div>

                        <div className="flex-grow-1 event-list-item-middle ps-1">
                            <div className="event-list-id event-list-bold">
                                {item.subject} {item.eventName}
                            </div>
                            <div className="event-list-secondary">
                                <div>{item.text}</div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>{DateTimeFormatter.formatTime(item.time)}</div>
                                    {extra.displayFlightStatus && item.type === EventListItemType.aircraft && (
                                        <FlightStatusBadge flightStatus={item.flightStatus} ground={item.ground} />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex flex-column justify-content-between align-self-stretch">
                            <Button
                                fillMode="flat"
                                className="event-list-button"
                                svgIcon={CloseOutline}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    extra.onDismiss(item);
                                }}
                            />
                            {item.ack && !item.ackTime && (
                                <Button
                                    fillMode="flat"
                                    className="event-list-button"
                                    svgIcon={CheckmarkOutline}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        extra.onAcknowledge(item);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </td>
            </tr>
        );
    };
}

function RowExpanded(extra: {
    colorMode: EventListBackgroundColorMode;
    displayMonitorStatus: boolean;
    displayImportantIndicator: boolean;
    displayFlightStatus: boolean;
    onClick: (e: MouseEvent, item: EventListItem) => void;
    onAcknowledge: (item: EventListItem) => void;
    onDismiss: (item: EventListItem) => void;
    onContextMenu: (e: MouseEvent, item: EventListItem) => void;
}) {
    return (props: GridCustomRowProps) => {
        const item = props.dataItem as EventListItem & { eventName: string };

        return (
            <tr {...props.trProps}>
                <td
                    style={getStyle(item, extra.colorMode)}
                    className="p-0 clickable border-bottom"
                    onClick={(e) => extra.onClick(e.nativeEvent, item)}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        extra.onContextMenu(e.nativeEvent, item);
                    }}>
                    <div className="event-list-item event-list-expanded d-flex gap-1 pe-0 align-items-stretch">
                        <div className="d-flex gap-1 flex-grow-1 min-w-0 py-1">
                            {extra.displayMonitorStatus && (
                                <div className="align-self-stretch">
                                    <MonitorStatusStripe className="h-100" monitorStatus={item.monitorStatus} />
                                </div>
                            )}

                            <div className="d-flex gap-1 align-items-center flex-grow-1">
                                {extra.displayImportantIndicator && (
                                    <div className="flex-shrink-0 event-list-important" style={{ color: item.color }}>
                                        {item.important && '!'}
                                    </div>
                                )}
                                <div
                                    className="flex-shrink-0 d-flex flex-column gap-0 align-items-start justify-content-center event-list-bold"
                                    style={{ width: 60 }}>
                                    {item.subject}
                                </div>

                                {extra.displayFlightStatus && (
                                    <div className="flex-shrink-0" style={{ width: 52 }}>
                                        {item.type === EventListItemType.aircraft && (
                                            <FlightStatusBadge flightStatus={item.flightStatus} ground={item.ground} />
                                        )}
                                    </div>
                                )}

                                <div
                                    className="flex-shrink-0 event-list-id event-list-expanded"
                                    style={{ width: 110, textTransform: 'capitalize' }}>
                                    {item.eventName}
                                </div>

                                <div className="flex-grow-1 flex-shrink-1 event-list-secondary d-flex gap-1">
                                    <div>
                                        {item.previousMonitorStatus === MonitorStatus.null
                                            ? item.monitorStatus === MonitorStatus.null
                                                ? ''
                                                : monitorStatusText[item.monitorStatus]
                                            : `${monitorStatusText[item.previousMonitorStatus]} → ${monitorStatusText[item.monitorStatus]}`}
                                    </div>
                                    <div>{!item.previousValue ? item.value : `${item.previousValue} → ${item.value}`}</div>
                                </div>

                                <div className="event-list-secondary flex-shrink-0" style={{ width: 50 }}>
                                    {DateTimeFormatter.formatTime(item.time)}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-self-center flex-column ps-1">
                            <Button
                                fillMode="flat"
                                className="event-list-button"
                                svgIcon={CloseOutline}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    extra.onDismiss(item);
                                }}
                            />
                            {item.ack && !item.ackTime && (
                                <Button
                                    fillMode="flat"
                                    className="event-list-button"
                                    svgIcon={CheckmarkOutline}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        extra.onAcknowledge(item);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </td>
            </tr>
        );
    };
}

function getStyle(item: EventListItem, mode: EventListBackgroundColorMode): React.CSSProperties | undefined {
    if (mode === EventListBackgroundColorMode.importantBackground) {
        if (!item.color || !item.important || item.ackTime) {
            return undefined;
        }

        return {
            backgroundColor: `color-mix(in srgb, ${item.color} 80%, transparent 100%)`
        };
    }

    if (mode === EventListBackgroundColorMode.monitorStatusBackground) {
        return {
            backgroundColor: monitorStatusBgColor[item.monitorStatus]
        };
    }

    return undefined;
}

export const monitorStatusBgColor: Record<MonitorStatus, string> = {
    [MonitorStatus.null]: '',
    [MonitorStatus.clear]: '',
    [MonitorStatus.info]: 'color-mix(in srgb, var(--monitor-status-info) 60%, transparent 100%)',
    [MonitorStatus.watch]: 'color-mix(in srgb, var(--monitor-status-watch) 60%, transparent 100%)',
    [MonitorStatus.warning]: 'color-mix(in srgb, var(--monitor-status-warning) 60%, transparent 100%)',
    [MonitorStatus.critical]: 'color-mix(in srgb, var(--monitor-status-critical) 60%, transparent 100%)',
    [MonitorStatus.emergency]: 'color-mix(in srgb, var(--monitor-status-emergency) 60%, transparent 100%)'
};
const monitorStatusText: Record<MonitorStatus, string> = {
    [MonitorStatus.null]: '',
    [MonitorStatus.clear]: 'Clear',
    [MonitorStatus.info]: 'Info',
    [MonitorStatus.watch]: 'Watch',
    [MonitorStatus.warning]: 'Warning',
    [MonitorStatus.critical]: 'Critical',
    [MonitorStatus.emergency]: 'Emergency'
};
